import {
  GoogleMemberExternalAuthStartResult,
  MemberExternalAccountCalendarManualImportInput,
  MemberExternalAccountCalendarManualImportResult,
  MemberExternalAccountImportPreferencesSafe,
  MemberExternalAccountImportPreferencesUpdateInput,
  MemberExternalAccountMessageManualImportInput,
  MemberExternalAccountMessageManualImportResult,
  MemberExternalAccountCalendarsResult,
  MemberExternalAccountDisconnectResult,
  MemberExternalAccountListResult,
  MemberExternalAccountMessageSyncBeaconResult,
  MailboxApplyLabelsBody,
  MailboxApplyLabelsResult,
  MailboxCreateLabelBody,
  MailboxCreateLabelResult,
  MailboxImportBody,
  MailboxImportResult,
  MailboxImportedRefsBody,
  MailboxImportedRefsResult,
  MailboxListFoldersResult,
  MailboxReadAttachmentResult,
  MailboxReadAttachmentRouteQuery,
  MailboxReadMessageResult,
  MailboxReadThreadResult,
  MailboxReadThreadRouteQuery,
  MailboxSearchResult,
  MailboxSearchRouteQuery,
} from '@vivport/backend/types';
import { httpClient } from './http-client';

export async function startGoogleMemberExternalConnect(): Promise<GoogleMemberExternalAuthStartResult> {
  return httpClient.post<GoogleMemberExternalAuthStartResult>('/protected/member-external-accounts/google/start');
}

export async function startMicrosoftMemberExternalConnect(): Promise<GoogleMemberExternalAuthStartResult> {
  return httpClient.post<GoogleMemberExternalAuthStartResult>('/protected/member-external-accounts/microsoft/start');
}

export async function listMemberExternalAccounts(): Promise<MemberExternalAccountListResult> {
  return httpClient.get<MemberExternalAccountListResult>('/protected/member-external-accounts/');
}

export async function sendMessageSyncBeacon(): Promise<MemberExternalAccountMessageSyncBeaconResult> {
  return httpClient.post<MemberExternalAccountMessageSyncBeaconResult>(
    '/protected/member-external-accounts/message-sync-beacon'
  );
}

export async function disconnectMemberExternalAccount(
  id: number
): Promise<MemberExternalAccountDisconnectResult> {
  return httpClient.delete<MemberExternalAccountDisconnectResult>(
    `/protected/member-external-accounts/${encodeURIComponent(String(id))}`
  );
}

export async function manualImportMessagesForMemberExternalAccount(
  id: number,
  input: MemberExternalAccountMessageManualImportInput
): Promise<MemberExternalAccountMessageManualImportResult> {
  const result = await httpClient.post<MemberExternalAccountMessageManualImportResult, MemberExternalAccountMessageManualImportInput>(
    `/protected/member-external-accounts/${encodeURIComponent(String(id))}/messages/manual-import`,
    input
  );
  return result;
}

export async function manualImportCalendarForAccount(
  id: number,
  input: MemberExternalAccountCalendarManualImportInput
): Promise<MemberExternalAccountCalendarManualImportResult> {
  return httpClient.post<MemberExternalAccountCalendarManualImportResult, MemberExternalAccountCalendarManualImportInput>(
    `/protected/member-external-accounts/${encodeURIComponent(String(id))}/calendar/manual-import`,
    input
  );
}

export async function listCalendarsForAccount(
  id: number
): Promise<MemberExternalAccountCalendarsResult> {
  return httpClient.get<MemberExternalAccountCalendarsResult>(
    `/protected/member-external-accounts/${encodeURIComponent(String(id))}/calendars`
  );
}

export async function updateMemberExternalAccountImportPreferences(
  id: number,
  input: MemberExternalAccountImportPreferencesUpdateInput
): Promise<MemberExternalAccountImportPreferencesSafe> {
  return httpClient.put<MemberExternalAccountImportPreferencesSafe, MemberExternalAccountImportPreferencesUpdateInput>(
    `/protected/member-external-accounts/${encodeURIComponent(String(id))}/import-preferences`,
    input
  );
}

function accountPath(id: number): string {
  return `/protected/member-external-accounts/${encodeURIComponent(String(id))}`;
}

export async function listMailboxFoldersForAccount(id: number): Promise<MailboxListFoldersResult> {
  return httpClient.get<MailboxListFoldersResult>(`${accountPath(id)}/mailbox/folders`);
}

export async function searchMailboxForAccount(
  id: number,
  params: MailboxSearchRouteQuery
): Promise<MailboxSearchResult> {
  return httpClient.get<MailboxSearchResult>(`${accountPath(id)}/mailbox/search`, { params });
}

export async function readMailboxMessageForAccount(
  id: number,
  providerMessageId: string
): Promise<MailboxReadMessageResult> {
  return httpClient.get<MailboxReadMessageResult>(
    `${accountPath(id)}/mailbox/messages/${encodeURIComponent(providerMessageId)}`
  );
}

export async function readMailboxThreadForAccount(
  id: number,
  providerThreadId: string,
  params?: MailboxReadThreadRouteQuery
): Promise<MailboxReadThreadResult> {
  return httpClient.get<MailboxReadThreadResult>(
    `${accountPath(id)}/mailbox/threads/${encodeURIComponent(providerThreadId)}`,
    { params }
  );
}

export async function readMailboxAttachmentTextForAccount(
  id: number,
  providerMessageId: string,
  attachmentId: string,
  params?: MailboxReadAttachmentRouteQuery
): Promise<MailboxReadAttachmentResult> {
  return httpClient.get<MailboxReadAttachmentResult>(
    `${accountPath(id)}/mailbox/messages/${encodeURIComponent(providerMessageId)}/attachments/${encodeURIComponent(attachmentId)}/text`,
    { params }
  );
}

export async function importMailboxMessagesForAccount(
  id: number,
  body: MailboxImportBody
): Promise<MailboxImportResult> {
  return httpClient.post<MailboxImportResult, MailboxImportBody>(`${accountPath(id)}/mailbox/import`, body);
}

export async function listMailboxImportedRefsForAccount(
  id: number,
  body: MailboxImportedRefsBody
): Promise<MailboxImportedRefsResult> {
  return httpClient.post<MailboxImportedRefsResult, MailboxImportedRefsBody>(
    `${accountPath(id)}/mailbox/imported-refs`,
    body
  );
}

export async function createMailboxLabelForAccount(
  id: number,
  body: MailboxCreateLabelBody
): Promise<MailboxCreateLabelResult> {
  return httpClient.post<MailboxCreateLabelResult, MailboxCreateLabelBody>(`${accountPath(id)}/mailbox/labels`, body);
}

export async function applyMailboxLabelsForAccount(
  id: number,
  body: MailboxApplyLabelsBody
): Promise<MailboxApplyLabelsResult> {
  return httpClient.post<MailboxApplyLabelsResult, MailboxApplyLabelsBody>(
    `${accountPath(id)}/mailbox/labels/apply`,
    body
  );
}
